import { useEffect, useState } from "react";
import { CalendarDays, Video, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { OFFICE } from "../data/content";

const MODE_ICONS = { in_person: MapPin, virtual: Video, phone: Phone };
const MODE_LABELS = { in_person: "In person", virtual: "Video call", phone: "Phone" };
const STATUS_COLORS = {
  REQUESTED: "bg-champagne-light text-champagne-dark",
  CONFIRMED: "bg-sage-light text-sage",
  COMPLETED: "bg-teal-light text-teal",
  CANCELLED: "bg-red-950/60 text-red-300",
};

export default function Appointments() {
  const [appointments, setAppointments] = useState([]);
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState({ service_type: "", mode: "", date: "", time: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/appointments").then((r) => setAppointments(r.data)).catch(() => {});

  useEffect(() => {
    load();
    api.get("/meta/options").then((r) => setTypes(r.data.appointment_types)).catch(() => {});
  }, []);

  const book = async () => {
    if (!form.service_type || !form.mode || !form.date || !form.time) {
      toast.error("Please choose a type, mode, date, and time.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/appointments", form);
      toast.success("Appointment requested. We will confirm shortly.");
      setForm({ service_type: "", mode: "", date: "", time: "", notes: "" });
      load();
    } catch {
      toast.error("Could not request the appointment.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="portal-appointments-page">
      <div>
        <h1 className="text-2xl font-serif font-semibold text-navy tracking-tight">Appointments</h1>
        <p className="mt-1 text-sm text-mutedink">Book time with your Full Circle team — in person in Fort Lauderdale, by video, or by phone.</p>
      </div>

      <section className="bg-surface border border-line rounded-2xl p-6 shadow-sm" data-testid="book-appointment-form">
        <h2 className="text-base font-sans font-semibold text-navy mb-5 flex items-center gap-2"><CalendarDays className="h-4 w-4 text-teal" /> Book an appointment</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Appointment type *</Label>
            <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
              <SelectTrigger className="mt-1.5" data-testid="appt-type-select"><SelectValue placeholder="Choose a type" /></SelectTrigger>
              <SelectContent>
                {types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>How would you like to meet? *</Label>
            <div className="mt-1.5 grid grid-cols-3 gap-2">
              {Object.entries(MODE_LABELS).map(([v, label]) => {
                const Icon = MODE_ICONS[v];
                return (
                  <button
                    key={v}
                    onClick={() => setForm({ ...form, mode: v })}
                    data-testid={`appt-mode-${v}`}
                    className={`rounded-xl border px-2 py-3 text-xs font-medium flex flex-col items-center gap-1.5 transition-all ${form.mode === v ? "border-teal bg-teal-light text-teal" : "border-line bg-ivory text-navy hover:border-teal"}`}
                  >
                    <Icon className="h-4 w-4" /> {label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <Label htmlFor="appt-date">Date *</Label>
            <Input id="appt-date" type="date" data-testid="appt-date-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="appt-time">Time *</Label>
            <Input id="appt-time" type="time" data-testid="appt-time-input" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="mt-1.5" />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="appt-notes">Anything we should know? (optional)</Label>
            <Textarea id="appt-notes" rows={2} data-testid="appt-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" />
          </div>
        </div>
        <Button onClick={book} disabled={saving} className="mt-5 bg-teal text-white hover:bg-teal-dark hover:text-white rounded-full" data-testid="appt-book-button">
          {saving ? "Requesting..." : "Request Appointment"}
        </Button>
        <p className="mt-3 text-xs text-mutedink">In-person appointments meet at {OFFICE.address}, {OFFICE.city}.</p>
      </section>

      <section data-testid="appointments-list">
        <h2 className="text-base font-sans font-semibold text-navy mb-4">Your appointments</h2>
        {appointments.length === 0 ? (
          <p className="text-sm text-mutedink bg-surface border border-dashed border-line rounded-2xl p-8 text-center">No appointments yet.</p>
        ) : (
          <div className="space-y-3">
            {appointments.map((a) => {
              const Icon = MODE_ICONS[a.mode] || CalendarDays;
              return (
                <div key={a.appt_id} className="bg-surface border border-line rounded-xl p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`appt-row-${a.appt_id}`}>
                  <div className="h-10 w-10 rounded-xl bg-teal-light flex items-center justify-center flex-shrink-0">
                    <Icon className="h-5 w-5 text-teal" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-navy">{a.service_type}</p>
                    <p className="text-xs text-mutedink mt-0.5">{MODE_LABELS[a.mode] || a.mode} · {a.date} at {a.time}</p>
                  </div>
                  <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full w-fit ${STATUS_COLORS[a.status] || "bg-sand text-mutedink"}`} data-testid={`appt-status-${a.appt_id}`}>{a.status}</span>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
